import { useState, useEffect, useCallback, useRef } from 'react'
import { useUser } from '@clerk/clerk-react'
import {
  getAdminDemoVideos, uploadDemoVideo, renameDemoVideo, deleteDemoVideo, rerunDemoVideo,
  type AdminDemoVideo,
} from '../lib/api'

const ADMIN_EMAILS = ['matt@primateintelligence.ai', 'mehdi@primateintelligence.ai']

// ── Video Library ─────────────────────────────────────────────────────────────

async function extractThumbnail(file: File): Promise<string | undefined> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    let resolved = false;
    function captureFrame() {
      if (resolved) return;
      try {
        const canvas = document.createElement('canvas');
        const aspectRatio = video.videoWidth > 0 && video.videoHeight > 0 ? video.videoWidth / video.videoHeight : 16 / 9;
        canvas.width = 160;
        canvas.height = Math.round(160 / aspectRatio);
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const px = ctx.getImageData(80, 45, 1, 1).data;
        if (px[0] === 0 && px[1] === 0 && px[2] === 0) return;
        resolved = true;
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      } catch { /* ignore */ }
    }
    video.muted = true; video.playsInline = true; video.preload = 'auto';
    video.src = url; video.load();
    video.onloadedmetadata = () => { video.currentTime = Math.min(Math.max(video.duration * 0.1, 0.5), 3); };
    video.onseeked = () => {
      let attempts = 0;
      captureFrame();
      const iv = setInterval(() => {
        captureFrame();
        if (++attempts > 10 || resolved) {
          clearInterval(iv);
          if (!resolved) { resolved = true; URL.revokeObjectURL(url); resolve(undefined); }
        }
      }, 200);
    };
    video.onerror = () => { URL.revokeObjectURL(url); resolve(undefined); };
    setTimeout(() => { if (!resolved) { resolved = true; URL.revokeObjectURL(url); resolve(undefined); } }, 10000);
  });
}

function VideoStatusBadge({ status }: { status: AdminDemoVideo['status'] }) {
  const map = { uploading: 'bg-yellow-500/20 text-yellow-400', scanning: 'bg-blue-500/20 text-blue-400', ready: 'bg-green-500/20 text-green-400', error: 'bg-red-500/20 text-red-400' }
  return <span className={`px-1.5 py-0.5 rounded text-footnote font-medium ${map[status]}`}>{status}</span>
}

function VideoLibraryBlock() {
  const [videos, setVideos] = useState<AdminDemoVideo[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [editId, setEditId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [savingId, setSavingId] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const loadVideos = useCallback(async () => {
    setLoading(true)
    try { const data = await getAdminDemoVideos(); setVideos(data.videos) }
    catch { /* silent */ }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { void loadVideos() }, [loadVideos])

  // Poll status for scanning/uploading videos
  useEffect(() => {
    const scanningIds = videos.filter(v => v.status === 'uploading' || v.status === 'scanning').map(v => v.id)
    if (scanningIds.length === 0) { if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; } return; }
    if (pollingRef.current) return;
    pollingRef.current = setInterval(async () => {
      try {
        const data = await getAdminDemoVideos()
        setVideos(data.videos)
        const stillScanning = data.videos.filter(v => v.status === 'uploading' || v.status === 'scanning')
        if (stillScanning.length === 0 && pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
      } catch { /* ignore */ }
    }, 5000)
    return () => { if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; } }
  }, [videos])

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    setUploading(true); setUploadError(null)
    try {
      const fd = new FormData()
      fd.append('video', file)
      fd.append('display_name', file.name.replace(/\.[^.]+$/, ''))
      // Extract thumbnail client-side before uploading
      const thumb = await extractThumbnail(file)
      if (thumb) fd.append('thumbnail_data_url', thumb)
      await uploadDemoVideo(fd)
      await loadVideos()
    } catch (err) { setUploadError((err as Error).message) }
    finally { setUploading(false) }
  }

  async function saveRename(id: string) {
    if (!editName.trim()) return
    setSavingId(id)
    try { await renameDemoVideo(id, editName.trim()); setEditId(null); await loadVideos() }
    catch (err) { alert((err as Error).message) }
    finally { setSavingId(null) }
  }

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Delete demo video "${name}" and all its results?`)) return
    setSavingId(id)
    try { await deleteDemoVideo(id); await loadVideos() }
    catch (err) { alert((err as Error).message) }
    finally { setSavingId(null) }
  }

  async function handleRerun(id: string) {
    setSavingId(id)
    try { await rerunDemoVideo(id); await loadVideos() }
    catch (err) { alert((err as Error).message) }
    finally { setSavingId(null) }
  }

  return (
    <div className="bg-obsidian border border-carbon rounded p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-h2 text-chalk">Video Library</h2>
          <p className="text-body-sm text-steel mt-1">Curated demo videos shown in the Demo Videos tab. Each video is pre-processed against all prompts.</p>
        </div>
        <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
          className="px-4 py-2 text-body-sm bg-signal text-white rounded hover:bg-signal/90 disabled:opacity-50 flex-shrink-0">
          {uploading ? 'Uploading…' : '+ Add video'}
        </button>
        <input ref={fileInputRef} type="file" accept="video/*" style={{ display: 'none' }} onChange={handleFileChange} />
      </div>

      {uploadError && <div className="text-footnote text-red-400 bg-red-900/20 rounded px-3 py-2">{uploadError}</div>}

      {loading ? (
        <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-signal" /></div>
      ) : videos.length === 0 ? (
        <div className="text-footnote text-steel text-center py-6">No demo videos. Upload one to get started.</div>
      ) : (
        <div className="bg-void border border-carbon rounded overflow-y-auto" style={{ maxHeight: 480 }}>
          {videos.map(v => (
            <div key={v.id} className="flex items-center gap-3 px-3 py-3 border-b border-carbon last:border-0 group hover:bg-carbon/20">
              {/* Thumbnail */}
              <div className="w-14 h-8 rounded bg-carbon flex-shrink-0 overflow-hidden" style={{display:"flex",alignItems:"center",justifyContent:"center"}}>
                {v.thumbnail_url ? <img src={v.thumbnail_url} alt="" style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain"}} /> : <div className="w-full h-full flex items-center justify-center text-steel text-footnote">🎬</div>}
              </div>

              {/* Name + status */}
              <div className="flex-1 min-w-0">
                {editId === v.id ? (
                  <div className="flex items-center gap-2">
                    <input autoFocus value={editName} onChange={e => setEditName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') void saveRename(v.id); if (e.key === 'Escape') setEditId(null); }}
                      className="flex-1 bg-carbon border border-signal rounded px-2 py-0.5 text-body-sm text-chalk focus:outline-none min-w-0" />
                    <button onClick={() => void saveRename(v.id)} disabled={savingId === v.id} className="px-2 py-0.5 text-footnote bg-signal text-white rounded">Save</button>
                    <button onClick={() => setEditId(null)} className="px-2 py-0.5 text-footnote text-steel border border-carbon rounded">✕</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-body-sm text-chalk font-medium truncate">{v.display_name}</span>
                    <VideoStatusBadge status={v.status} />
                    {v.status === 'ready' && v.demo_video_results && (
                      <span className="text-footnote text-steel">{v.demo_video_results.length} results</span>
                    )}
                  </div>
                )}
                {v.error_message && <div className="text-footnote text-red-400 mt-0.5">{v.error_message}</div>}
                {v.file_size_bytes && <div className="text-footnote text-steel">{(v.file_size_bytes / 1024 / 1024).toFixed(1)} MB</div>}
              </div>

              {/* Actions */}
              {editId !== v.id && (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => { setEditId(v.id); setEditName(v.display_name); }} className="px-2 py-0.5 text-footnote text-signal border border-signal/30 rounded hover:bg-signal/10">Rename</button>
                  {v.status === 'ready' && (
                    <button onClick={() => void handleRerun(v.id)} disabled={savingId === v.id} className="px-2 py-0.5 text-footnote text-steel border border-carbon rounded hover:text-chalk disabled:opacity-50">Re-run</button>
                  )}
                  <button onClick={() => void handleDelete(v.id, v.display_name)} disabled={savingId === v.id} className="px-2 py-0.5 text-footnote text-red-400 border border-red-400/30 rounded hover:bg-red-400/10 disabled:opacity-50">Delete</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="text-footnote text-steel">Videos are pre-processed against all prompts automatically on upload. Re-run to refresh after adding new prompts.</p>
    </div>
  )
}


// ── Model Info ────────────────────────────────────────────────────────────────

interface ModelInfo {
  model_name: string
  vl_checkpoint?: string
  jepa_checkpoint?: string
  vl_step?: number
  jepa_step?: number
  status?: string
}

interface CheckpointInfo {
  name: string
  step: number
  size_mb: number
}

// PRI-271: centralized API base URL (was VITE_API_URL fallback; consolidated to VITE_API_BASE_URL via base.ts)
// PRI-496 P0: admin endpoints are internal — route through the chokepoint.
import { internalFetch } from '../core/lib/internal-api-client'
import { getAuthToken } from '../core/lib/api/client'
import { MODEL_DISPLAY_NAME } from '../core/components/v2/pv2-shared'

function ModelInfoBlock() {
  const [modelInfo, setModelInfo] = useState<ModelInfo | null>(null)
  const [checkpoints, setCheckpoints] = useState<CheckpointInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [switching, setSwitching] = useState(false)
  const [switchResult, setSwitchResult] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      setLoading(true)
      try {
        // PRI (2026-06-26): public /v1/model-info no longer returns checkpoint
        // detail. Use the admin-gated proxy with the Clerk bearer token.
        const token = await getAuthToken()
        const authHeaders = token ? { Authorization: `Bearer ${token}` } : undefined
        const [miRes, ckRes] = await Promise.all([
          internalFetch(`/v1/model-info-proxy`, { headers: authHeaders }),
          internalFetch(`/v1/admin/checkpoints`, { headers: authHeaders }),
        ])
        if (miRes.ok) setModelInfo(await miRes.json())
        if (ckRes.ok) {
          const data = await ckRes.json()
          setCheckpoints(data.checkpoints ?? [])
        }
      } catch { /* silent */ }
      finally { setLoading(false) }
    })()
  }, [])

  async function switchCheckpoint(name: string) {
    if (!confirm(`Switch to checkpoint ${name}? The inference server will restart and be unavailable for ~4 minutes.`)) return
    setSwitching(true); setSwitchResult(null)
    try {
      const res = await internalFetch(`/v1/admin/reload-checkpoint`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vl_checkpoint: name }),
      })
      const data = await res.json()
      setSwitchResult(data.message ?? (res.ok ? 'Reload triggered' : 'Error'))
    } catch (err) {
      setSwitchResult((err as Error).message)
    } finally {
      setSwitching(false)
    }
  }

  return (
    <div className="bg-obsidian border border-carbon rounded p-6 space-y-4">
      <div>
        <h2 className="text-h2 text-chalk">Model</h2>
        <p className="text-body-sm text-steel mt-1">Current inference model and available checkpoints.</p>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-signal" /></div>
      ) : (
        <>
          {modelInfo && (
            <div className="bg-void border border-carbon rounded p-4 space-y-2">
              <div className="flex items-center gap-3">
                {/* Canonical public model name (matches the home-screen Advanced menu).
                    The inference server's internal checkpoint label is shown below. */}
                <span className="text-body-sm font-medium text-chalk">{MODEL_DISPLAY_NAME}</span>
                {modelInfo.status === 'loading' ? (
                  <span className="px-1.5 py-0.5 rounded text-footnote bg-yellow-500/20 text-yellow-400">loading</span>
                ) : (
                  <span className="px-1.5 py-0.5 rounded text-footnote bg-green-500/20 text-green-400">ready</span>
                )}
              </div>
              {modelInfo.vl_checkpoint && (
                <div className="text-footnote text-steel font-mono">VL: {modelInfo.vl_checkpoint} {modelInfo.vl_step ? `(step ${modelInfo.vl_step.toLocaleString()})` : ''}</div>
              )}
              {modelInfo.jepa_checkpoint && (
                <div className="text-footnote text-steel font-mono">JEPA: {modelInfo.jepa_checkpoint} {modelInfo.jepa_step ? `(step ${modelInfo.jepa_step.toLocaleString()})` : ''}</div>
              )}
            </div>
          )}
          {switchResult && (
            <div className="text-footnote text-blue-300 bg-blue-900/20 rounded px-3 py-2">{switchResult}</div>
          )}
          {checkpoints.length > 0 ? (
            <div>
              <div className="text-footnote text-steel mb-2">Available checkpoints</div>
              <div className="bg-void border border-carbon rounded overflow-y-auto" style={{ maxHeight: 300 }}>
                {checkpoints.map(ck => (
                  <div key={ck.name} className="flex items-center gap-3 px-3 py-2 border-b border-carbon last:border-0 group hover:bg-carbon/20">
                    <div className="flex-1 min-w-0">
                      <div className="text-body-sm text-chalk font-mono truncate">{ck.name}</div>
                      <div className="text-footnote text-steel">step {ck.step.toLocaleString()} · {ck.size_mb} MB</div>
                    </div>
                    {modelInfo?.vl_checkpoint === ck.name ? (
                      <span className="px-2 py-0.5 text-footnote bg-green-500/20 text-green-400 rounded flex-shrink-0">active</span>
                    ) : (
                      <button
                        onClick={() => void switchCheckpoint(ck.name)}
                        disabled={switching}
                        className="px-2 py-0.5 text-footnote text-signal border border-signal/30 rounded hover:bg-signal/10 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50 flex-shrink-0"
                      >
                        {switching ? 'Switching…' : 'Switch'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-footnote text-steel">No checkpoints found or inference server unavailable.</div>
          )}
        </>
      )}
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AdminDemoLibrary() {
  const { user, isLoaded } = useUser()
  const email = user?.primaryEmailAddress?.emailAddress ?? user?.emailAddresses?.[0]?.emailAddress ?? ''
  const isAdmin = isLoaded && !!user && ADMIN_EMAILS.includes(email)

  if (!isLoaded) return <div className="min-h-screen bg-void" />
  if (!isAdmin) return (
    <div className="min-h-screen bg-void flex items-center justify-center">
      <p className="text-body text-steel">Access denied</p>
    </div>
  )

  return (
    <div className="flex-1 p-8 text-chalk">
      <div className="max-w-2xl space-y-8">
        <div>
          <h1 className="text-h1 text-chalk">Demo Library</h1>
          <p className="text-body-sm text-steel mt-1">Manage demo videos shown in the Primate Vision demo experience.</p>
        </div>
        <VideoLibraryBlock />
        <ModelInfoBlock />
      </div>
    </div>
  )
}
